import { HEADER_PX, isRecordVisibleIn, type Band } from './recordVisibility';

/**
 * «Какие записи на экране» для сигнала у строки (ТЗ главной § 8, «Данные») — без React и DOM, его собирают
 * тесты; DOM подставляет `useRowsInView`. Два наблюдателя только будят: IntersectionObserver (корень `main`,
 * снизу вычтена полоса создания) — при прокрутке и смене видимости, ResizeObserver — когда запись меняет высоту
 * (новая строка въезжает 0 → 64, раскрывается или сворачивается тело). Решение — по живой геометрии на момент
 * колбэка (`geometry`), а не по снимку наблюдателя:
 * - строка, выросшая целиком внутри корня, не меняет долю пересечения (она всё время 1), и IntersectionObserver
 *   больше не приходит — её первое наблюдение при высоте 0 оставалось «не на экране» навсегда, и сигнал висел
 *   над видимой записью (приёмка главной D, A2-01);
 * - снимок IntersectionObserver, доставленный после следующего кадра, не откатывает более свежий ответ.
 *
 * Здесь же — «строка доехала» (`arrived`): впервые получила полную высоту заголовка. С этого момента сигнал у
 * строки решает, видна ли запись (§ 8): по таймеру от появления в DOM решение при редких кадрах (медленное
 * устройство, свёрнутое окно) успевало раньше, чем строка получала высоту, а конец анимации въезда не наступает,
 * когда AnimatePresence подавил её (первая запись приходит вместе с секцией).
 */

export interface ObserverHandle<T> {
  observe(target: T): void;
  unobserve(target: T): void;
  disconnect(): void;
}

export interface RecordGeometry {
  /** Прямоугольник записи. */
  box: Band;
  /** Предки между записью и корнем, которые обрезают содержимое (`overflow`). */
  clips: Band[];
  /** Видимая полоса корня: от верха `main` до верха полосы создания. */
  band: Band;
}

export interface RowsTrackerEnv<T> {
  /** Наблюдатель пересечений; `dockHeight` — сколько снизу закрыто полосой (`rootMargin`). Нет API — null. */
  createIntersection: (wake: (targets: readonly T[]) => void, dockHeight: number) => ObserverHandle<T> | null;
  /** Наблюдатель размеров. Нет API — null. */
  createResize: (wake: (targets: readonly T[]) => void) => ObserverHandle<T> | null;
  /** Живая геометрия записи; корня нет — null («не на экране»). */
  geometry: (target: T, dockHeight: number) => RecordGeometry | null;
  /** Запись на экране или нет; повтор того же значения модель слежения не замечает. */
  report: (id: string, visible: boolean) => void;
  /** Строка доехала — впервые получила полную высоту заголовка; по каждому id — один раз. */
  arrived?: (id: string) => void;
}

/** Строка доехала, когда её высота дошла до заголовка (64 px); полпикселя — на дробную раскладку. */
export const ARRIVED_PX = HEADER_PX - 0.5;

export interface RowsTracker<T> {
  /** Создать наблюдателей и взять под них уже зарегистрированные записи. */
  start: (dockHeight: number) => void;
  /** Высота полосы изменилась: `rootMargin` у живого наблюдателя не меняется — он пересоздаётся. */
  setDockHeight: (dockHeight: number) => void;
  /** Ref-колбэк записи: элемент появился, сменился или ушёл (`null` — «не на экране»). */
  register: (id: string, target: T | null) => void;
  stop: () => void;
}

export function createRowsTracker<T extends object>(env: RowsTrackerEnv<T>): RowsTracker<T> {
  const elements = new Map<string, T>();
  const ids = new WeakMap<T, string>();
  const arrivedIds = new Set<string>();
  let intersection: ObserverHandle<T> | null = null;
  let resize: ObserverHandle<T> | null = null;
  let dockHeight = 0;
  let running = false;

  const check = (id: string, target: T) => {
    const geometry = env.geometry(target, dockHeight);
    if (geometry && geometry.box.height >= ARRIVED_PX && !arrivedIds.has(id)) {
      arrivedIds.add(id);
      env.arrived?.(id);
    }
    env.report(id, geometry !== null && isRecordVisibleIn(geometry.box, geometry.clips, geometry.band));
  };
  const wake = (targets: readonly T[]) => {
    for (const target of targets) {
      const id = ids.get(target);
      // Колбэк по элементу, который уже сменился или ушёл, ничего не решает.
      if (id !== undefined && elements.get(id) === target) check(id, target);
    }
  };
  const observeAll = (observer: ObserverHandle<T> | null) => {
    if (observer) for (const target of elements.values()) observer.observe(target);
  };

  return {
    start(height) {
      if (running) return;
      running = true;
      dockHeight = height;
      intersection = env.createIntersection(wake, height);
      resize = env.createResize(wake);
      observeAll(intersection);
      observeAll(resize);
    },
    setDockHeight(height) {
      if (height === dockHeight) return;
      dockHeight = height;
      if (!running) return;
      intersection?.disconnect();
      intersection = env.createIntersection(wake, height);
      observeAll(intersection);
    },
    register(id, target) {
      const previous = elements.get(id);
      if (previous === target) return;
      if (previous !== undefined) {
        intersection?.unobserve(previous);
        resize?.unobserve(previous);
        elements.delete(id);
        env.report(id, false);
      }
      if (target) {
        elements.set(id, target);
        ids.set(target, id);
        intersection?.observe(target);
        resize?.observe(target);
      }
    },
    stop() {
      running = false;
      intersection?.disconnect();
      resize?.disconnect();
      intersection = null;
      resize = null;
    },
  };
}
